import { Card, CardContent } from '@/components/ui/shadcn/card'
import { Eye, Heart, BarChart3, TrendingUp } from 'lucide-react'
import type { RouterOutput } from '@/config/trpc'

type OverviewData = RouterOutput['analytics']['overview']

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toLocaleString()
}

export function SummaryCards({ data }: { data: OverviewData }) {
  const { aggregates, total } = data
  const totalEngagement =
    aggregates.totalLikes +
    aggregates.totalComments +
    aggregates.totalShares +
    aggregates.totalSaves

  const avgEngagementRate =
    aggregates.postsWithMetrics > 0 && aggregates.totalViews > 0
      ? ((totalEngagement / aggregates.totalViews) * 100).toFixed(1)
      : '0'

  const cards = [
    {
      title: 'Total Posts',
      value: formatNumber(total),
      icon: BarChart3,
      detail: `${aggregates.postsWithMetrics} with metrics`,
    },
    {
      title: 'Total Views',
      value: formatNumber(aggregates.totalViews),
      icon: Eye,
      detail:
        total > 0 ? `~${formatNumber(Math.round(aggregates.totalViews / total))} avg/post` : null,
    },
    {
      title: 'Total Engagement',
      value: formatNumber(totalEngagement),
      icon: Heart,
      detail: `${formatNumber(aggregates.totalLikes)} likes, ${formatNumber(aggregates.totalComments)} comments`,
    },
    {
      title: 'Engagement Rate',
      value: `${avgEngagementRate}%`,
      icon: TrendingUp,
      detail: 'Across all posts',
    },
  ]

  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
      {cards.map((card) => (
        <Card key={card.title} className="relative overflow-hidden">
          <CardContent className="space-y-0 pt-1">
            <div className="flex items-center justify-between">
              <p className="text-muted-foreground text-xs font-medium uppercase tracking-wide">
                {card.title}
              </p>
              <card.icon size={14} className="text-muted-foreground/50" />
            </div>
            <p className="text-foreground text-2xl font-semibold tracking-tight">{card.value}</p>
            {card.detail && <p className="text-muted-foreground/70 text-[11px]">{card.detail}</p>}
          </CardContent>
        </Card>
      ))}
    </div>
  )
}
